import products from './productHandler';
import { filter } from '../../util/propFilter';
import { applicationSettings } from '../../settings/application';
import rangeSettings from '../../settings/constants';
import store from '../../state';
import Bror from './bror';
import Jonaxel from './jonaxel';
import Boaxel from './boaxel';
import Aurdal from './aurdal';
import Ivar from './ivar';
import Elvarli from './elvarli';
import { ITEMS } from '../../constants';
import constants from '../../settings/constants';
import { selectCurrentFilterName } from '../../state/productMenu/productMenuSelectors';
import { selectTac } from '../../state/tac/tacSelectors';

let rangeApi;

function getRangeApi(appName) {
  switch (appName) {
    case 'BROR':
      return Bror;
    case 'JONAXEL':
      return Jonaxel;
    case 'BOAXEL':
      return Boaxel;
    case 'AURDAL':
      return Aurdal;
    case 'IVAR':
      return Ivar;
    case 'ELVARLI':
      return Elvarli;
    default:
      throw new Error(
        'Missing implementation of range specific product service'
      );
  }
}

function init() {
  rangeApi = getRangeApi(applicationSettings.applicationName);
}

function makeArticleImages() {
  rangeApi.makeArticleImages();
}

function isStackable(item) {
  return rangeApi.isStackable && rangeApi.isStackable(item);
}

function getArticleImages() {
  return rangeApi.articleImages;
}

function getArticleImage(id) {
  return rangeApi.getArticleImage(id);
}
function getCustomProductTypeText(product) {
  return rangeApi.getCustomArticleTypeText?.(product);
}

function isOnlyAvailableInMultipack(productId) {
  const multipackProductId = constants.BULK_ARTICLES?.[productId]?.id;
  if (!multipackProductId) return false;

  const isAvailableAndValid = id =>
    products.some(product => product.id === id && product.valid);
  const productIsValid = isAvailableAndValid(productId);
  const multipackProductIsValid = isAvailableAndValid(multipackProductId);

  const isMultipackOnly = multipackProductIsValid && !productIsValid;
  return isMultipackOnly;
}

/**
 * Gets the known products.
 * Note that invalid products are normally not returned, unless they are invalid
 * products for which there is a corresponding valid multipack product. In that case,
 * they are returned even if includeAllInvalid is false. When includeAllInvalid is true,
 * all known products are returned regardless of whether they are valid or invalid.
 * @param includeAllInvalid Whether to also return invalid products
 *                          even when they are not "multipack only".
 * @returns {TacItem[]} The products.
 */
function getAll(includeAllInvalid = false) {
  if (includeAllInvalid) {
    return products;
  }
  return products.filter(
    product => product.valid || isOnlyAvailableInMultipack(product.id)
  );
}

/**
 * Gets the product with the specified id.
 * Note that invalid products are normally not included in the search, unless they are
 * invalid products for which there is a corresponding valid multipack product. In that
 * case, they are included even if includeAllInvalid is false. When includeAllInvalid is
 * true, all invalid articles are included in the search.
 * @param id The id of the product to get.
 * @param includeAllInvalid Whether to also search for invalid products
 *                          even when they are not "multipack only".
 * @returns {TacItem | undefined} The product, if found. Otherwise undefined.
 */
function getProduct(id, includeAllInvalid = false) {
  if (id.filter && (id.valid || isOnlyAvailableInMultipack(id.id))) {
    // This is already a "complete" and valid product obj, no need to do extra lookup.
    return id;
  }
  if (id.id) {
    id = id.id;
  }
  return getAll(includeAllInvalid).find(product => product.id === id);
}

function getMultipackProductOf(productId) {
  const multipackProductId = constants.BULK_ARTICLES?.[productId]?.id;
  if (!multipackProductId) return null;

  return getProduct(multipackProductId);
}

function getFilteredItems(selector, ...filters) {
  let items = getAll().filter(selector);

  if (filters) {
    items = filter(items, ...filters);
  }

  return items;
}

function getIncludedArticles(productId, list = []) {
  const product = getProduct(productId, true);

  if (!product) {
    return;
  }

  if (product.iows) {
    for (let i = 0; i < product.iows.length; i++) {
      const itemno = product.iows[i].itemno;
      list.push(itemno);
    }
  }

  if (product.parts) {
    Object.values(product.parts).forEach(part => {
      getIncludedArticles(part, list);
    });
  }
  return list;
}

function getSectionInserts(...filters) {
  return rangeApi.getSectionInserts
    ? rangeApi.getSectionInserts(filters)
    : null;
}

function isAddOnShelf(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.ADD_ON;
}

function isCabinet(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.CABINET;
}

function isPegboard(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.PEGBOARD;
}

function isSection(id) {
  return rangeApi.isSection(id);
}

function getSections(...filters) {
  return getFilteredItems(isSection, ...filters);
}

function isShelf(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.SHELF;
}

function isDrawer(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.DRAWER;
}

function getShelves(...filters) {
  return getFilteredItems(isShelf, ...filters);
}

function getDrawers(...filters) {
  return getFilteredItems(isDrawer, ...filters);
}

function isTable(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.TABLE;
}

function isTrolley(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.TROLLEY;
}

function isWorkbench(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.WORKBENCH;
}

function isShelvingUnit(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.SHELVING_UNIT;
}

function isFrame(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.FRAME;
}

function isLeg(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === 'leg';
}

function isUpright(id) {
  const found = getProduct(id, true);
  return found && found.filter.type === ITEMS.UPRIGHT;
}

function isExtendable(id) {
  const found = getProduct(id, true);
  return found && found.logic && found.logic.extendable;
}

function isType(id, typeOrTypes) {
  const types = [].concat(typeOrTypes);
  const found = getProduct(id, true);
  return found && types.some(type => type === found.filter.type);
}

function isModule(id) {
  return rangeApi.isModule(id);
}

function isInsert(id) {
  return rangeApi.isInsert(id);
}

function getFit(item, slot) {
  return rangeApi.getFit(item, slot);
}

function getDragMode(product) {
  return rangeApi.getDragMode(product);
}

function getSwappablesOfType(typeOrTypes, ...filters) {
  function isOfCorrectType(candidate) {
    return isType(candidate, typeOrTypes);
  }
  return getFilteredItems(isOfCorrectType, ...filters);
}

function getSwappables(item, ...filters) {
  return getSwappablesOfType(item.filter.type, ...filters);
}

function isMultiParentProduct(item) {
  return rangeApi.isMultiParentProduct
    ? rangeApi.isMultiParentProduct(item)
    : false;
}

function isRealArticleProduct(id) {
  const found = getProduct(id, true);
  return found && found.iows && found.iows.length;
}

const showInfoIcon = id =>
  rangeApi.showInfoIcon ? rangeApi.showInfoIcon(id) : isRealArticleProduct(id);

function getFittingBracket(product, side, color) {
  return rangeApi.getFittingBracket
    ? rangeApi.getFittingBracket(product, side, color)
    : null;
}

function areMountingRailsOfSpecificColorValid(color) {
  return rangeApi.areMountingRailsOfSpecificColorValid
    ? rangeApi.areMountingRailsOfSpecificColorValid(color)
    : false;
}

function getValidMountingRailColors() {
  return rangeApi.getValidMountingRailColors
    ? rangeApi.getValidMountingRailColors()
    : null;
}

function areMountingRailsValid() {
  return rangeApi.areMountingRailsValid
    ? rangeApi.areMountingRailsValid()
    : false;
}

function isDependentItem(item) {
  return rangeApi.isDependentItem ? rangeApi.isDependentItem(item) : false;
}

function isAdjustable(item) {
  return rangeApi.isAdjustable ? rangeApi.isAdjustable(item) : false;
}

function getSectionOffset(item) {
  return rangeApi.getSectionOffset
    ? rangeApi.getSectionOffset(item)
    : { x: 0, y: 0, z: 0 };
}

function shouldHaveColorSelector(item) {
  return rangeApi.shouldHaveColorSelector?.(item);
}

function shouldHaveChildColorSelector(item) {
  return rangeApi.shouldHaveChildColorSelector
    ? rangeApi.shouldHaveChildColorSelector(item)
    : isSection(item);
}

function shouldHaveHeadlineForSelectors(item) {
  return rangeApi.shouldHaveHeadlineForSelectors?.(item) || false;
}

function shouldShowDoorHint(item) {
  return rangeApi.shouldShowDoorHint?.(item) || false;
}

function getInitialZPos(item) {
  return rangeApi.getInitialZPos?.(item) || 0;
}

function hasAllMandatoryProductsOfColor(color) {
  return rangeApi.hasAllMandatoryProductsOfColor
    ? rangeApi.hasAllMandatoryProductsOfColor(color)
    : true;
}

function getMissingMandatoryProductCategories(filter) {
  return rangeApi.getMissingMandatoryProductCategories
    ? rangeApi.getMissingMandatoryProductCategories(filter)
    : [];
}

function fitsTable(section) {
  return rangeApi.fitsTable?.(section) || false;
}

function shouldIgnoreFloorAndCeilingCollision(item) {
  return rangeApi?.shouldIgnoreFloorAndCeilingCollision?.(item) || false;
}

function canHaveOverlappingDropAreas(item) {
  return rangeApi?.canHaveOverlappingDropAreas?.(item);
}

const generateProppingImageResources = imageList =>
  imageList.reduce((acc, image) => {
    if (!image?.proppingBounds.length) return acc;

    const proppingImageResources = image.proppingBounds.map((img, i) => ({
      name: `${image.name}_propping_${i + 1}`,
      url: `${rangeSettings.IMAGE_ROOT}${image.modelid}_propping_${i + 1}_${
        image.color
      }_0001.png`,
    }));

    return [...acc, ...proppingImageResources];
  }, imageList);

const deriveMeasurements = products => {
  const { depths, widths, heights } = products.reduce(
    ({ depths, widths, heights }, { depth, width, height }) => ({
      depths: depth ? depths.add(depth) : depths,
      widths: width ? widths.add(width) : widths,
      heights: height ? heights.add(height) : heights,
    }),
    { depths: new Set(), widths: new Set(), heights: new Set() }
  );

  return { depth: [...depths], width: [...widths], height: [...heights] };
};

const getPostWidth = () =>
  rangeApi?.getPostWidth ? rangeApi?.getPostWidth() : constants.POST_WIDTH;

/**
 * Gets a matching shelf for a specific section
 * @param {*} section
 * @returns {Object | undefined}
 */
const getFittingShelf = section => {
  return rangeApi?.getFittingShelf?.(section);
};

/**
 * The general thought with the items filter system is that each item-filter-object
 * has 4 keys: 'Component', 'condition', 'filter' and 'appliesTo'. Each is explained below.
 *
 * The 'Component' is simply a react component used to handle nad update the filter.
 *
 * 'condition' decides whether or not said filter should have its component
 * rendered as well as whether or not to run the actual filtering functionality.
 * In case of returning false, the filter will not be applied on the productMenuItems,
 * and the filter-component will not be rendered.
 *
 * 'filter' is a curry function which takes the redux-state as the first argument
 * where it can then derive and  prep any needed redux data for the coming
 * filter function. The second part is the actual filter, where it takes an item
 * and must return a boolean, which will determine whether or not said item will be
 * part of productMenu.
 *
 * 'appliesTo' is a way to only apply said filter-function to a specific swiper filter.
 * It is an array in which you put the names of the filters to which the filter-function
 * is to be applied. In case 'appliesTo' is left empty, the filter-function should apply
 * to all filters.
 *
 * This way we can reuse filter functionality and combine different filter functionality
 * with different filter components. If a specific combination is only supposed to be
 * rendered for a certain filter group, such as 'Sections' or 'Inserts', the 'condition'
 * part can be used to single out when the filter is to be applied.
 *
 * @returns {*|*[]}
 */
const getFilters = () => {
  const filters = rangeApi.getFilters() ? rangeApi.getFilters() : [];

  return filters.filter(({ condition, appliesTo }) => {
    const currentFilter = selectCurrentFilterName(store.getState());

    return (
      (!appliesTo.length || appliesTo.includes(currentFilter)) && condition()
    );
  });
};

const isWallDependentSection = section => {
  return section.items?.some(item => item.logic.ceiling);
};

const getWallHeightDependentSections = allItems => {
  return allItems?.filter(item => isWallDependentSection(item));
};

const getWallHeightDependentParts = sections => {
  return sections.reduce((acc, curr) => {
    return [
      ...acc,
      ...curr.items.reduce((acc, curr) => {
        return curr.logic.ceiling ? [...acc, curr] : acc;
      }, []),
    ];
  }, []);
};

export const getWallHeightDependentItems = () => {
  const tac = selectTac(store.getState());
  const allItems = tac.items;
  return {
    sections: getWallHeightDependentSections(allItems),
    parts: getWallHeightDependentParts(
      getWallHeightDependentSections(allItems)
    ),
  };
};

const hasMissingBrackets = product => {
  return rangeApi?.hasMissingBrackets
    ? rangeApi.hasMissingBrackets(product)
    : false;
};

const getColorsOfProducts = products =>
  products.reduce(
    (colorsThisFar, product) =>
      colorsThisFar.includes(product.filter.color)
        ? colorsThisFar
        : [...colorsThisFar, product.filter.color],
    []
  );

export {
  filter,
  getAll,
  getArticleImage,
  getArticleImages,
  getCustomProductTypeText,
  getFittingBracket,
  getProduct,
  getSections,
  getShelves,
  getDrawers,
  hasAllMandatoryProductsOfColor,
  init,
  isAddOnShelf,
  isAdjustable,
  isCabinet,
  isModule,
  isInsert,
  isPegboard,
  isSection,
  isShelf,
  isDrawer,
  isTable,
  isTrolley,
  isWorkbench,
  isShelvingUnit,
  isFrame,
  isLeg,
  isUpright,
  isExtendable,
  isType,
  isStackable,
  products,
  getFilteredItems,
  getFit,
  getDragMode,
  getIncludedArticles,
  getSwappablesOfType,
  getSwappables,
  isMultiParentProduct,
  isRealArticleProduct,
  areMountingRailsOfSpecificColorValid,
  getValidMountingRailColors,
  areMountingRailsValid,
  isDependentItem,
  getSectionOffset,
  shouldHaveColorSelector,
  shouldHaveChildColorSelector,
  shouldHaveHeadlineForSelectors,
  shouldShowDoorHint,
  getInitialZPos,
  getMissingMandatoryProductCategories,
  fitsTable,
  shouldIgnoreFloorAndCeilingCollision,
  canHaveOverlappingDropAreas,
  generateProppingImageResources,
  deriveMeasurements,
  getFittingShelf,
  getFilters,
  getPostWidth,
  hasMissingBrackets,
  isOnlyAvailableInMultipack,
  getMultipackProductOf,
  getColorsOfProducts,
};

export default {
  filter,
  getAll,
  getArticleImage,
  getArticleImages,
  getCustomProductTypeText,
  getFittingBracket,
  getProduct,
  getSections,
  getShelves,
  hasAllMandatoryProductsOfColor,
  init,
  isAddOnShelf,
  isAdjustable,
  isCabinet,
  isModule,
  isInsert,
  isPegboard,
  isSection,
  isShelf,
  isDrawer,
  isTable,
  isTrolley,
  isWorkbench,
  isShelvingUnit,
  isFrame,
  isUpright,
  isExtendable,
  isType,
  isLeg,
  isStackable,
  products,
  getFilteredItems,
  getFit,
  getDragMode,
  getIncludedArticles,
  getSectionInserts,
  getSwappablesOfType,
  getSwappables,
  isMultiParentProduct,
  isRealArticleProduct,
  areMountingRailsOfSpecificColorValid,
  getValidMountingRailColors,
  areMountingRailsValid,
  isDependentItem,
  getSectionOffset,
  shouldHaveColorSelector,
  shouldHaveChildColorSelector,
  shouldHaveHeadlineForSelectors,
  getInitialZPos,
  getMissingMandatoryProductCategories,
  fitsTable,
  shouldIgnoreFloorAndCeilingCollision,
  makeArticleImages,
  canHaveOverlappingDropAreas,
  getFittingShelf,
  showInfoIcon,
  getFilters,
  getPostWidth,
  hasMissingBrackets,
  isOnlyAvailableInMultipack,
  getMultipackProductOf,
  getColorsOfProducts,
};
